/**
 * Dynamicgraph.jsx — Refactored
 *
 * Key structural improvements:
 *  1. Global modal architecture — single `globalModal` state at the top level;
 *     `openModal(config)` / `closeModal()` passed down as props so TablePopUp,
 *     PopUpData and NestedTablePopUp (row-level) all render ONE fixed overlay
 *     at the root, not trapped inside card or table-row DOM nodes.
 *  2. All debug code removed (console.log, window.data).
 *  3. Modern inline-style design system (C tokens) — clean cards, sticky header,
 *     status badges, visual hierarchy.
 *  4. All data fields, states, display_type branches, chart logic, and export
 *     functions are preserved exactly.
 */

// import React, { useCallback, useEffect, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { AgCharts } from "ag-charts-react";
// import { article_details } from "../api";
// import LoadingScreen from "react-loading-screen";
// import html2canvas from "html2canvas";
// import { toast } from "react-toastify";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import jsPDF from "jspdf";

// // ─────────────────────────────────────────────────────────────────────────────
// // § 1  DESIGN TOKENS  (single source of truth for all colours / radii)
// // ─────────────────────────────────────────────────────────────────────────────
// const C = {
//   navy: "#0E2954",
//   navyLight: "#1A3F7A",
//   amber: "#F5A623",
//   amberLight: "#FEF5E0",
//   pass: "#059669",
//   passLight: "#D1FAE5",
//   fail: "#DC2626",
//   failLight: "#FEE2E2",
//   indigo: "#6366F1",
//   indigoLight: "#EEF2FF",
//   warn: "#D97706",
//   warnLight: "#FEF3C7",
//   bg: "#F3F5F9",
//   card: "#FFFFFF",
//   border: "#E5E7EB",
//   row: "#F9FAFB",
//   textPrimary: "#111827",
//   textMuted: "#6B7280",
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // § 2  PURE UI PRIMITIVES  (no state, safe to define outside the component)
// // ─────────────────────────────────────────────────────────────────────────────

// /** Generic white card with subtle shadow */
// const Card = ({ children, style = {} }) => (
//   <div
//     style={{
//       backgroundColor: C.card,
//       borderRadius: 16,
//       padding: 20,
//       boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
//       border: `1px solid ${C.border}`,
//       ...style,
//     }}
//   >
//     {children}
//   </div>
// );

// /** Section heading with amber left-bar accent + check-count pill */
// const SectionHeader = ({ title, count }) => (
//   <div
//     style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}
//   >
//     <div
//       style={{
//         width: 4,
//         height: 34,
//         backgroundColor: C.amber,
//         borderRadius: 2,
//         flexShrink: 0,
//       }}
//     />
//     <h2
//       style={{
//         color: C.navy,
//         fontSize: 19,
//         fontWeight: 800,
//         margin: 0,
//         letterSpacing: "-0.01em",
//       }}
//     >
//       {title}
//     </h2>
//     {count !== undefined && (
//       <span
//         style={{
//           backgroundColor: C.amberLight,
//           color: C.amber,
//           fontSize: 11,
//           fontWeight: 700,
//           padding: "2px 10px",
//           borderRadius: 999,
//           letterSpacing: "0.05em",
//         }}
//       >
//         {count} checks
//       </span>
//     )}
//   </div>
// );

// /** Thin amber divider used inside metric cards */
// const AmberDivider = () => (
//   <div
//     style={{
//       height: 2,
//       backgroundColor: C.amberLight,
//       borderRadius: 1,
//       margin: "8px 0",
//     }}
//   />
// );

// /** Green ✓ / Red ✗ pill badge */
// const StatusBadge = ({ value }) => {
//   const isTrue = String(value ?? "").toLowerCase() === "true";
//   return (
//     <span
//       style={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 5,
//         padding: "5px 14px",
//         borderRadius: 999,
//         fontSize: 13,
//         fontWeight: 700,
//         backgroundColor: isTrue ? C.passLight : C.failLight,
//         color: isTrue ? C.pass : C.fail,
//       }}
//     >
//       {isTrue ? "✓ True" : "✗ False"}
//     </span>
//   );
// };

// /** Large numeric / text value for metric cards */
// const BigValue = ({ children, small }) => (
//   <span
//     style={{
//       fontSize: small ? 18 : 30,
//       fontWeight: 900,
//       color: C.navy,
//       fontVariantNumeric: "tabular-nums",
//       lineHeight: 1,
//     }}
//   >
//     {children}
//   </span>
// );

// /** Standard metric card: label (top) → amber divider → centered value */
// const CheckCard = ({ title, children, style = {} }) => (
//   <Card
//     style={{
//       display: "flex",
//       flexDirection: "column",
//       gap: 8,
//       minHeight: 120,
//       ...style,
//     }}
//   >
//     <p
//       style={{
//         fontSize: 11,
//         fontWeight: 700,
//         color: C.textMuted,
//         textTransform: "uppercase",
//         letterSpacing: "0.06em",
//         lineHeight: 1.5,
//         margin: 0,
//       }}
//     >
//       {title}
//     </p>
//     <AmberDivider />
//     <div
//       style={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flex: 1,
//         paddingTop: 4,
//       }}
//     >
//       {children}
//     </div>
//   </Card>
// );

// /** Reusable "Details →" trigger button */
// const DetailsBtn = ({ onClick }) => (
//   <button
//     onClick={onClick}
//     style={{
//       backgroundColor: C.amber,
//       color: "#fff",
//       border: "none",
//       cursor: "pointer",
//       borderRadius: 8,
//       padding: "5px 12px",
//       fontSize: 11,
//       fontWeight: 700,
//       flexShrink: 0,
//     }}
//   >
//     Details →
//   </button>
// );

// /** Styled table header cell */
// const Th = ({ children }) => (
//   <th
//     style={{
//       padding: "8px 12px",
//       textAlign: "left",
//       color: C.textMuted,
//       fontWeight: 700,
//       fontSize: 11,
//       textTransform: "uppercase",
//       letterSpacing: "0.05em",
//       borderBottom: `1px solid ${C.border}`,
//       whiteSpace: "nowrap",
//     }}
//   >
//     {children}
//   </th>
// );

// /** Styled table data cell */
// const Td = ({ children, center }) => (
//   <td
//     style={{
//       padding: "8px 12px",
//       color: C.textPrimary,
//       fontSize: 13,
//       borderBottom: `1px solid ${C.border}`,
//       textAlign: center ? "center" : "left",
//     }}
//   >
//     {children}
//   </td>
// );

// // ─────────────────────────────────────────────────────────────────────────────
// // § 3  DATA HELPERS
// // ─────────────────────────────────────────────────────────────────────────────

// /** Resolve a numeric / text count from a check item */
// const resolveCount = (data) => {
//   if (
//     Array.isArray(data?.content) &&
//     data.content.length > 0 &&
//     data.content[0]?.count !== undefined
//   ) {
//     const c = data.content[0].count;
//     return c === 0 ? "N/A" : c;
//   }
//   if (data?.count !== undefined) return data.count === 0 ? "N/A" : data.count;
//   return null;
// };

// /** Resolve a true/false string from a check item */
// const resolveIfExists = (data) => {
//   if (Array.isArray(data?.content) && data.content[0]?.ifexists !== undefined)
//     return String(data.content[0].ifexists);
//   if (data?.ifexits !== undefined) return String(data.ifexits);
//   return null;
// };

// /** Safely stringify any nested value for table cells */
// const renderValue = (val) => {
//   if (val == null || val === "NULL") return "N/A";
//   if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : "—";
//   if (typeof val === "object") return JSON.stringify(val);
//   return String(val);
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // § 4  MAIN COMPONENT
// // ─────────────────────────────────────────────────────────────────────────────
// const Dynamicgraph = ({ id }) => {
//   // ── State ──────────────────────────────────────────────────────────────────
//   const [data, setData] = useState([]);
//   const [category, setCategory] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [excelData, setExcelData] = useState([]);
//   const [fileStatus, setFileStatus] = useState("");

//   /**
//    * GLOBAL MODAL STATE
//    * null  → closed
//    * { title: string, body: ReactNode, indexing?: { title1, title2 } } → open
//    *
//    * Every sub-component calls openModal(config) instead of managing its own
//    * modal state. The overlay is rendered once, at root level.
//    */
//   const [globalModal, setGlobalModal] = useState(null);
//   const openModal = useCallback((cfg) => setGlobalModal(cfg), []);
//   const closeModal = useCallback(() => setGlobalModal(null), []);

//   const navigate = useNavigate();
//   const pdfRef = useRef();

//   // ── Data fetch ─────────────────────────────────────────────────────────────
//   useEffect(() => {
//     fetchreport();
//   }, [id]);

//   useEffect(() => {
//     if (data) {
//       setExcelData(
//         data.map((item) => ({
//           CheckNo: item.check,
//           CheckTitle: item.check_title,
//           Count: item.count,
//         })),
//       );
//     }
//   }, [data]);

//   const fetchreport = async () => {
//     const TIMEOUT = 50000;
//     const timeoutPromise = new Promise((_, reject) =>
//       setTimeout(() => reject(new Error("Timeout")), TIMEOUT),
//     );
//     try {
//       const result = await Promise.race([article_details(id), timeoutPromise]);
//       if (result.data) {
//         setFileStatus(result?.data?.file_progress);
//         setLoading(false);
//         setData(result?.data?.report);
//         setCategory(result?.data?.category);
//       } else {
//         toast.warn(result.error.message);
//         navigate("/filehistory");
//       }
//     } catch (e) {
//       if (e.message === "Timeout") {
//         toast.warn("Response took longer than expected, Redirecting ...");
//         navigate("/filehistory");
//       } else {
//         console.error(e?.response?.data?.message || e.message);
//       }
//     }
//   };

//   // ── Group data by category_id ──────────────────────────────────────────────
//   const sortedReport =
//     data?.length > 0
//       ? data.reduce((group, item) => {
//           if (!group[item.category_id]) group[item.category_id] = [];
//           group[item.category_id].push(item);
//           return group;
//         }, {})
//       : {};

//   // ── Exports ────────────────────────────────────────────────────────────────
//   const exportToExcel = () => {
//     const worksheet = XLSX.utils.json_to_sheet(excelData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
//     const buf = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
//     saveAs(
//       new Blob([buf], { type: "application/octet-stream" }),
//       "report.xlsx",
//     );
//   };

//   const generatePDF = () => {
//     html2canvas(pdfRef.current, { scale: 2 }).then((canvas) => {
//       const imgData = canvas.toDataURL("image/png");
//       const pdf = new jsPDF("p", "mm", "a4");
//       const pdfW = pdf.internal.pageSize.getWidth();
//       const pdfH = pdf.internal.pageSize.getHeight();
//       const imgH = (canvas.height * pdfW) / canvas.width;
//       let left = imgH;
//       let pos = 0;
//       pdf.addImage(imgData, "PNG", 0, pos, pdfW, imgH);
//       left -= pdfH;
//       while (left > 0) {
//         pos = left - imgH;
//         pdf.addPage();
//         pdf.addImage(imgData, "PNG", 0, pos, pdfW, imgH);
//         left -= pdfH;
//       }
//       pdf.save("report.pdf");
//     });
//   };

//   // ─────────────────────────────────────────────────────────────────────────
//   // § 5  CHART COMPONENTS
//   //      Defined inside to stay close to the original structure.
//   //      Charts do not interact with the global modal.
//   // ─────────────────────────────────────────────────────────────────────────

//   const Chart = ({ data: chartData }) => {
//     const [options] = useState({
//       background: { fill: "transparent" },
//       title: { text: "Checks", fontSize: 16, fontWeight: 600, color: C.navy },
//       data: chartData,
//       series: [
//         {
//           type: "bar",
//           direction: "horizontal",
//           xKey: "check_title",
//           yKey: "count",
//           yName: "Checks",
//           fill: C.amber,
//           cornerRadius: 4,
//           label: {
//             enabled: true,
//             color: C.textPrimary,
//             fontSize: 12,
//             placement: "outside",
//             formatter: ({ value }) => String(value),
//           },
//         },
//       ],
//       axes: [
//         {
//           type: "category",
//           position: "left",
//           label: { fontSize: 11, color: C.textMuted },
//         },
//         {
//           type: "number",
//           position: "bottom",
//           label: { fontSize: 11, color: C.textMuted },
//         },
//       ],
//       height: 380,
//     });
//     return <AgCharts options={options} />;
//   };

//   const Referencehistogram = ({ data: rawData }) => {
//     let parsed = null;
//     try {
//       if (rawData?.content?.[0]?.content?.length > 1) {
//         parsed = rawData.content[0].content.map((entry) => {
//           const key = Object.keys(entry)[0];
//           return { name: key.trim(), value: parseInt(entry[key].trim()) };
//         });
//       }
//     } catch {
//       /* silent */
//     }

//     const [options] = useState({
//       background: { fill: "transparent" },
//       title: {
//         text: "Reference Year Distribution",
//         fontSize: 16,
//         fontWeight: 600,
//         color: C.navy,
//       },
//       data: parsed ?? [],
//       series: [
//         {
//           type: "bar",
//           xKey: "name",
//           yKey: "value",
//           fill: C.indigo,
//           cornerRadius: 4,
//           label: {
//             enabled: true,
//             color: C.textPrimary,
//             fontSize: 12,
//             placement: "outside",
//             formatter: ({ value }) => String(value),
//           },
//         },
//       ],
//       height: 380,
//     });

//     return parsed ? <AgCharts options={options} /> : null;
//   };

//   const Tablegraph = ({ data: rawData }) => {
//     const [options, setOptions] = useState({
//       background: { fill: "transparent" },
//       title: {
//         text: Array.isArray(rawData) ? "Report" : (rawData?.check_title ?? ""),
//       },
//       legend: { position: "bottom" },
//       series: [],
//     });

//     useEffect(() => {
//       if (!rawData) return;
//       const chartData = Array.isArray(rawData)
//         ? rawData
//         : [{ check_title: rawData.check_title, count: rawData.count }];
//       if (!chartData.length) return;
//       setOptions((prev) => ({
//         ...prev,
//         title: {
//           text: chartData.length === 1 ? chartData[0].check_title : "Report",
//         },
//         series: [
//           {
//             type: "donut",
//             angleKey: "count",
//             calloutLabelKey: "check_title",
//             sectorLabelKey: "count",
//             innerRadiusRatio: 0.5,
//             data: chartData,
//             fills: [C.indigo, C.amber, "#FFD54F", "#4CAF50"],
//           },
//         ],
//       }));
//     }, [rawData]);

//     return <AgCharts options={options} />;
//   };

//   // ─────────────────────────────────────────────────────────────────────────
//   // § 6  DISPLAY-TYPE CARD COMPONENTS
//   //      Each calls openModal() for detail views — no local modal state.
//   // ─────────────────────────────────────────────────────────────────────────

//   /** Fallback when no value is resolvable */
//   const MailComponent = () => (
//     <span style={{ fontSize: 12, color: C.textMuted, fontStyle: "italic" }}>
//       No data
//     </span>
//   );

//   // ── count ──────────────────────────────────────────────────────────────────
//   const CountData = ({ data }) => {
//     const val = resolveCount(data);
//     return (
//       <CheckCard title={data.check_title}>
//         {val !== null ? (
//           <BigValue small={String(val).length > 5}>{val}</BigValue>
//         ) : (
//           <MailComponent />
//         )}
//       </CheckCard>
//     );
//   };

//   // ── true_false ─────────────────────────────────────────────────────────────
//   const TrueFalseData = ({ data }) => {
//     const val = resolveIfExists(data);
//     return (
//       <CheckCard title={data.check_title}>
//         {val !== null ? <StatusBadge value={val} /> : <MailComponent />}
//       </CheckCard>
//     );
//   };

//   // ── count_list / true_false_list  (with list content modal) ────────────────
//   const PopUpData = ({ data, title, indexing, check, type }) => {
//     const val =
//       type === "truefalse" ? resolveIfExists(data) : resolveCount(data);
//     const hasContent =
//       (Array.isArray(data?.content?.[0]?.content) &&
//         data.content[0].content.length > 0) ||
//       (Array.isArray(data?.content) &&
//         data.content.length > 0 &&
//         data.content[0]?.content !== "" &&
//         data.content[0]?.content !== undefined);

//     const handleOpen = () => {
//       const body = (
//         <div style={{ overflowY: "auto", maxHeight: 360, padding: "0 2px" }}>
//           {Array.isArray(data?.content) ? (
//             <ul
//               style={{
//                 listStyle: "disc",
//                 paddingLeft: 20,
//                 display: "flex",
//                 flexDirection: "column",
//                 gap: 10,
//                 margin: 0,
//               }}
//             >
//               {Array.isArray(data.content?.[0]?.content)
//                 ? data.content[0].content.map((item, i) => (
//                     <li
//                       key={i}
//                       style={{
//                         fontSize: 13,
//                         lineHeight: 1.6,
//                         color: C.textPrimary,
//                       }}
//                     >
//                       {item}
//                     </li>
//                   ))
//                 : data.content.map((item, i) => (
//                     <li
//                       key={i}
//                       style={{
//                         fontSize: 13,
//                         lineHeight: 1.6,
//                         color: C.textPrimary,
//                       }}
//                     >
//                       {item.content}
//                     </li>
//                   ))}
//             </ul>
//           ) : (
//             <p style={{ fontSize: 13, color: C.textPrimary, margin: 0 }}>
//               {data?.content || "No content available"}
//             </p>
//           )}
//         </div>
//       );
//       openModal({ title, indexing, body });
//     };

//     return (
//       <CheckCard title={data?.check_title}>
//         <div
//           style={{
//             display: "flex",
//             width: "100%",
//             alignItems: "center",
//             justifyContent: "space-between",
//             gap: 8,
//           }}
//         >
//           {type === "truefalse" ? (
//             val !== null ? (
//               <StatusBadge value={val} />
//             ) : (
//               <MailComponent />
//             )
//           ) : val !== null ? (
//             <BigValue small={String(val).length > 5}>{val}</BigValue>
//           ) : (
//             <MailComponent />
//           )}
//           {hasContent && <DetailsBtn onClick={handleOpen} />}
//         </div>
//       </CheckCard>
//     );
//   };

//   // ── count_table / true_false_table  (with table-in-modal) ─────────────────
//   const TablePopUp = ({ data, type, title, check }) => {
//     const val =
//       type === "truefalse" ? resolveIfExists(data) : resolveCount(data);

//     const isArrayContent = Array.isArray(data?.content);
//     const hasNestedContent = isArrayContent
//       ? data.content.some((item) => typeof item?.content === "object")
//       : false;

//     const tableHeaders = Array.isArray(data?.content?.[0]?.content)
//       ? [
//           ...new Set(
//             data.content[0].content.flatMap((obj) => Object.keys(obj || {})),
//           ),
//         ]
//       : [];

//     const hasTable = !!data?.content?.[0]?.content;

//     const handleOpen = () => {
//       const body = hasNestedContent ? (
//         <p style={{ color: C.fail, fontSize: 13, textAlign: "center" }}>
//           Nested content detected — please use a different display type.
//         </p>
//       ) : (
//         <div style={{ overflowY: "auto", maxHeight: 340, overflowX: "auto" }}>
//           <table style={{ width: "100%", borderCollapse: "collapse" }}>
//             {tableHeaders.length > 0 && (
//               <thead style={{ backgroundColor: C.row }}>
//                 <tr>
//                   {tableHeaders.map((k, i) => (
//                     <Th key={i}>{k}</Th>
//                   ))}
//                 </tr>
//               </thead>
//             )}
//             <tbody>
//               {Array.isArray(data?.content?.[0]?.content) &&
//               data.content[0].content.length > 0 ? (
//                 data.content[0].content.map((row, i) => (
//                   <tr key={i}>
//                     {tableHeaders.map((header, j) => (
//                       <Td key={j}>{row?.[header] ?? "N/A"}</Td>
//                     ))}
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td
//                     colSpan={tableHeaders.length || 1}
//                     style={{
//                       padding: 20,
//                       textAlign: "center",
//                       color: C.textMuted,
//                       fontSize: 13,
//                     }}
//                   >
//                     No data to display
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       );
//       openModal({ title, body });
//     };

//     return (
//       <CheckCard title={data?.check_title ?? "Untitled Check"}>
//         <div
//           style={{
//             display: "flex",
//             width: "100%",
//             alignItems: "center",
//             justifyContent: "space-between",
//             gap: 8,
//           }}
//         >
//           {type === "truefalse" ? (
//             val !== null ? (
//               <StatusBadge value={val} />
//             ) : (
//               <MailComponent />
//             )
//           ) : val !== null ? (
//             <BigValue small={String(val).length > 5}>{val}</BigValue>
//           ) : (
//             <MailComponent />
//           )}
//           {hasTable && <DetailsBtn onClick={handleOpen} />}
//         </div>
//       </CheckCard>
//     );
//   };

//   // ── table_popup  (full inline table; nested rows open a global modal) ──────
//   const NestedTablePopUp = ({ data, title }) => {
//     const isEmptyContent =
//       !data?.content ||
//       !Array.isArray(data.content) ||
//       data.content.length === 0;

//     // Determine top-level keys — respect check === 41 override
//     const allKeys =
//       data?.check === 41
//         ? ["id", "author", "orcid"].filter((k) =>
//             data?.content?.some((item) => k in (item || {})),
//           )
//         : Array.from(
//             new Set(
//               data?.content?.flatMap((item) => Object.keys(item || {})) || [],
//             ),
//           );

//     const hasContentKey = data?.content?.some(
//       (item) => "content" in (item || {}),
//     );
//     const displayKeys = hasContentKey
//       ? allKeys.filter((k) => k !== "content")
//       : allKeys;

//     /** True when a content field needs a drill-down modal */
//     const isNestedContent = (content) => {
//       if (!content) return false;
//       if (Array.isArray(content))
//         return content.some((i) => typeof i === "object" && !Array.isArray(i));
//       return typeof content === "object";
//     };

//     /**
//      * Build the JSX for whatever nested structure comes back.
//      * Handles: array-of-objects → table, array-of-primitives → list,
//      * plain object → single-row table, primitive → paragraph.
//      */
//     const buildNestedTable = (nested) => {
//       if (!nested || (Array.isArray(nested) && nested.length === 0))
//         return (
//           <p style={{ fontSize: 13, color: C.textMuted }}>
//             No relevant data found.
//           </p>
//         );

//       if (Array.isArray(nested) && nested.every((i) => typeof i === "object")) {
//         const keys = Array.from(
//           new Set(nested.flatMap((obj) => Object.keys(obj || {}))),
//         );
//         return (
//           <div style={{ overflowX: "auto" }}>
//             <table style={{ width: "100%", borderCollapse: "collapse" }}>
//               <thead style={{ backgroundColor: C.row }}>
//                 <tr>
//                   {keys.map((k) => (
//                     <Th key={k}>{k}</Th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {nested.map((obj, idx) => (
//                   <tr key={idx}>
//                     {keys.map((k) => (
//                       <Td key={k}>{renderValue(obj[k])}</Td>
//                     ))}
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         );
//       }

//       if (Array.isArray(nested)) {
//         return (
//           <ul
//             style={{
//               listStyle: "disc",
//               paddingLeft: 20,
//               margin: 0,
//               display: "flex",
//               flexDirection: "column",
//               gap: 6,
//             }}
//           >
//             {nested.map((item, idx) => (
//               <li key={idx} style={{ fontSize: 13, color: C.textPrimary }}>
//                 {renderValue(item)}
//               </li>
//             ))}
//           </ul>
//         );
//       }

//       if (typeof nested === "object") {
//         const keys = Object.keys(nested);
//         if (!keys.length)
//           return (
//             <p style={{ fontSize: 13, color: C.textMuted }}>
//               No relevant data found.
//             </p>
//           );
//         return (
//           <div style={{ overflowX: "auto" }}>
//             <table style={{ width: "100%", borderCollapse: "collapse" }}>
//               <thead style={{ backgroundColor: C.row }}>
//                 <tr>
//                   {keys.map((k) => (
//                     <Th key={k}>{k}</Th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 <tr>
//                   {keys.map((k) => (
//                     <Td key={k}>{renderValue(nested[k])}</Td>
//                   ))}
//                 </tr>
//               </tbody>
//             </table>
//           </div>
//         );
//       }

//       return (
//         <p style={{ fontSize: 13, color: C.textPrimary }}>
//           {renderValue(nested)}
//         </p>
//       );
//     };

//     /** Row-level "View →" opens the GLOBAL modal, not a trapped local one */
//     const handleRowModal = (nested, rowIndex) => {
//       openModal({
//         title: `${title} — Row ${rowIndex + 1}`,
//         body: (
//           <div style={{ overflowY: "auto", maxHeight: 380 }}>
//             {buildNestedTable(nested)}
//           </div>
//         ),
//       });
//     };

//     return (
//       <Card style={{ overflow: "hidden", padding: "18px 20px" }}>
//         {/* Card header */}
//         <p
//           style={{
//             fontSize: 11,
//             fontWeight: 700,
//             color: C.textMuted,
//             textTransform: "uppercase",
//             letterSpacing: "0.06em",
//             margin: "0 0 8px",
//           }}
//         >
//           {data?.check_title}
//         </p>
//         <AmberDivider />

//         <div style={{ overflowX: "auto", marginTop: 10 }}>
//           {isEmptyContent ? (
//             <p
//               style={{
//                 fontSize: 13,
//                 color: C.textMuted,
//                 textAlign: "center",
//                 padding: "12px 0",
//               }}
//             >
//               No data available
//             </p>
//           ) : (
//             <table style={{ width: "100%", borderCollapse: "collapse" }}>
//               <thead style={{ backgroundColor: C.row }}>
//                 <tr>
//                   {displayKeys.map((k) => (
//                     <Th key={k}>{k}</Th>
//                   ))}
//                   {hasContentKey && <Th>Details</Th>}
//                 </tr>
//               </thead>
//               <tbody>
//                 {data.content.map((row, rowIndex) => {
//                   const nested = row.content;
//                   const hasNested = isNestedContent(nested);
//                   return (
//                     <tr key={rowIndex}>
//                       {displayKeys.map((k) => (
//                         <Td key={k}>{renderValue(row[k])}</Td>
//                       ))}
//                       {hasContentKey && (
//                         <Td center>
//                           {hasNested ? (
//                             <button
//                               onClick={() => handleRowModal(nested, rowIndex)}
//                               style={{
//                                 backgroundColor: C.amber,
//                                 color: "#fff",
//                                 border: "none",
//                                 cursor: "pointer",
//                                 borderRadius: 6,
//                                 padding: "4px 12px",
//                                 fontSize: 11,
//                                 fontWeight: 700,
//                               }}
//                             >
//                               View →
//                             </button>
//                           ) : (
//                             <span
//                               style={{ fontSize: 12, color: C.textPrimary }}
//                             >
//                               {renderValue(nested)}
//                             </span>
//                           )}
//                         </Td>
//                       )}
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           )}
//         </div>
//       </Card>
//     );
//   };

//   // ─────────────────────────────────────────────────────────────────────────
//   // § 7  EMPTY-STATE CONFIG
//   // ─────────────────────────────────────────────────────────────────────────
//   const STATUS_CFG = {
//     1: {
//       icon: "⏳",
//       label: "Report generation has not started yet",
//       color: C.warn,
//       bg: C.warnLight,
//     },
//     2: {
//       icon: "⚙️",
//       label: "Report is being generated…",
//       color: C.indigo,
//       bg: C.indigoLight,
//     },
//   };
//   const emptyState = STATUS_CFG[fileStatus] ?? {
//     icon: "⚠️",
//     label: "Error generating report",
//     color: C.fail,
//     bg: C.failLight,
//   };

//   // ─────────────────────────────────────────────────────────────────────────
//   // § 8  RENDER
//   // ─────────────────────────────────────────────────────────────────────────
//   return (
//     <LoadingScreen
//       loading={loading}
//       bgColor={C.bg}
//       spinnerColor={C.amber}
//       textColor={C.navy}
//       text="Fetching report…"
//     >
//       {/* ════════════════════════════════════════════════════════════════════
//           GLOBAL MODAL OVERLAY
//           Rendered at the root so it is never clipped by overflow:hidden on
//           a parent card or trapped inside a <tr>. Uses fixed positioning and
//           a high z-index, with a blurred/dimmed backdrop.
//           ════════════════════════════════════════════════════════════════════ */}
//       {globalModal && (
//         <div
//           style={{
//             position: "fixed",
//             inset: 0,
//             zIndex: 9999,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             backgroundColor: "rgba(14, 41, 84, 0.55)",
//             backdropFilter: "blur(5px)",
//             WebkitBackdropFilter: "blur(5px)",
//             padding: 24,
//           }}
//         >
//           {/* Transparent backdrop — click to close */}
//           <div
//             onClick={closeModal}
//             style={{ position: "absolute", inset: 0 }}
//           />

//           {/* Modal panel */}
//           <div
//             style={{
//               position: "relative",
//               zIndex: 1,
//               backgroundColor: C.card,
//               borderRadius: 20,
//               padding: 28,
//               width: "100%",
//               maxWidth: 700,
//               maxHeight: "85vh",
//               display: "flex",
//               flexDirection: "column",
//               gap: 16,
//               boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
//             }}
//           >
//             {/* ── Modal header ── */}
//             <div
//               style={{
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "flex-start",
//                 gap: 12,
//               }}
//             >
//               <div
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 10,
//                   flex: 1,
//                   minWidth: 0,
//                 }}
//               >
//                 <div
//                   style={{
//                     width: 4,
//                     height: 28,
//                     backgroundColor: C.amber,
//                     borderRadius: 2,
//                     flexShrink: 0,
//                   }}
//                 />
//                 <h3
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 800,
//                     color: C.navy,
//                     margin: 0,
//                     lineHeight: 1.3,
//                     overflow: "hidden",
//                     textOverflow: "ellipsis",
//                     whiteSpace: "nowrap",
//                   }}
//                 >
//                   {globalModal.title}
//                 </h3>
//               </div>
//               <button
//                 onClick={closeModal}
//                 style={{
//                   backgroundColor: "#F3F4F6",
//                   color: C.textMuted,
//                   border: "none",
//                   cursor: "pointer",
//                   borderRadius: 8,
//                   padding: "7px 14px",
//                   fontSize: 13,
//                   fontWeight: 700,
//                   flexShrink: 0,
//                   transition: "background 0.15s",
//                 }}
//               >
//                 ✕ Close
//               </button>
//             </div>

//             {/* ── Optional legend (for list-type modals with indexing) ── */}
//             {globalModal.indexing && (
//               <div style={{ display: "flex", gap: 20 }}>
//                 <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
//                   <span
//                     style={{
//                       width: 10,
//                       height: 10,
//                       borderRadius: "50%",
//                       backgroundColor: C.fail,
//                       display: "inline-block",
//                       flexShrink: 0,
//                     }}
//                   />
//                   <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
//                     {globalModal.indexing.title1}
//                   </p>
//                 </div>
//                 <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
//                   <span
//                     style={{
//                       width: 10,
//                       height: 10,
//                       borderRadius: "50%",
//                       backgroundColor: C.pass,
//                       display: "inline-block",
//                       flexShrink: 0,
//                     }}
//                   />
//                   <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
//                     {globalModal.indexing.title2}
//                   </p>
//                 </div>
//               </div>
//             )}

//             {/* ── Divider ── */}
//             <div
//               style={{ height: 1, backgroundColor: C.border, flexShrink: 0 }}
//             />

//             {/* ── Scrollable body ── */}
//             <div style={{ overflowY: "auto", flex: 1 }}>{globalModal.body}</div>
//           </div>
//         </div>
//       )}

//       {/* ════════════════════════════════════════════════════════════════════
//           PAGE SHELL
//           ════════════════════════════════════════════════════════════════════ */}
//       <div
//         style={{
//           backgroundColor: C.bg,
//           minHeight: "100vh",
//           fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
//           color: C.textPrimary,
//         }}
//       >
//         {data?.length > 0 ? (
//           <>
//             {/* ── Sticky header ─────────────────────────────────────────── */}
//             <div
//               style={{
//                 backgroundColor: C.card,
//                 borderBottom: `1px solid ${C.border}`,
//                 padding: "18px 40px",
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//                 position: "sticky",
//                 top: 0,
//                 zIndex: 100,
//                 boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
//               }}
//             >
//               <div>
//                 <p
//                   style={{
//                     fontSize: 10,
//                     fontWeight: 700,
//                     color: C.amber,
//                     textTransform: "uppercase",
//                     letterSpacing: "0.12em",
//                     margin: "0 0 3px",
//                   }}
//                 >
//                   QUALITY ANALYSIS
//                 </p>
//                 <h1
//                   style={{
//                     fontSize: 22,
//                     fontWeight: 900,
//                     color: C.navy,
//                     margin: 0,
//                     letterSpacing: "-0.02em",
//                   }}
//                 >
//                   Article Report
//                 </h1>
//               </div>

//               <div style={{ display: "flex", gap: 10 }}>
//                 <button
//                   onClick={exportToExcel}
//                   style={{
//                     padding: "9px 18px",
//                     borderRadius: 9,
//                     border: `1.5px solid ${C.border}`,
//                     backgroundColor: C.card,
//                     color: C.textPrimary,
//                     fontSize: 13,
//                     fontWeight: 600,
//                     cursor: "pointer",
//                     boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
//                   }}
//                 >
//                   ↓ Export Excel
//                 </button>
//                 <button
//                   onClick={generatePDF}
//                   style={{
//                     padding: "9px 20px",
//                     borderRadius: 9,
//                     border: "none",
//                     background: `linear-gradient(135deg, ${C.navyLight}, ${C.navy})`,
//                     color: "#fff",
//                     fontSize: 13,
//                     fontWeight: 700,
//                     cursor: "pointer",
//                     boxShadow: "0 2px 8px rgba(14,41,84,0.28)",
//                   }}
//                 >
//                   ↓ Export PDF
//                 </button>
//               </div>
//             </div>

//             {/* ── Report sections (one per category) ────────────────────── */}
//             <div
//               ref={pdfRef}
//               style={{
//                 padding: "36px 40px",
//                 display: "flex",
//                 flexDirection: "column",
//                 gap: 52,
//               }}
//             >
//               {category.map((cat, i) => {
//                 const reports = sortedReport[cat.category] || [];
//                 if (!reports.length) return null;

//                 // ── Partition by display_type ──────────────────────────────
//                 const byType = (t) =>
//                   reports.filter(
//                     (r) => r?.display_type?.trim().toLowerCase() === t,
//                   );

//                 const charts = byType("chart");
//                 const counts = byType("count");
//                 const truefalse = byType("true_false");
//                 const truefalselist = byType("true_false_list");
//                 const truefalsetable = byType("true_false_table");
//                 const countlist = byType("count_list");
//                 const counttable = byType("count_table");
//                 const histogram = byType("histogram");
//                 const donut = byType("donut_chart");
//                 const tablewithpopup = byType("table_popup");

//                 const hasCards =
//                   counts.length +
//                     countlist.length +
//                     counttable.length +
//                     truefalse.length +
//                     truefalselist.length +
//                     truefalsetable.length >
//                   0;

//                 return (
//                   <section key={i}>
//                     <SectionHeader title={cat.value} count={reports.length} />

//                     <div
//                       style={{
//                         display: "flex",
//                         flexDirection: "column",
//                         gap: 20,
//                       }}
//                     >
//                       {/* Charts row */}
//                       {(charts.length > 0 ||
//                         histogram.length > 0 ||
//                         donut.length > 0) && (
//                         <div
//                           style={{
//                             display: "grid",
//                             gridTemplateColumns:
//                               "repeat(auto-fit, minmax(340px, 1fr))",
//                             gap: 20,
//                           }}
//                         >
//                           {charts.length > 0 && (
//                             <Card>
//                               <Chart data={charts} />
//                             </Card>
//                           )}
//                           {histogram.map((h, idx) => (
//                             <Card key={idx}>
//                               <Referencehistogram data={h} />
//                             </Card>
//                           ))}
//                           {donut.length > 0 && (
//                             <Card>
//                               <Tablegraph data={donut} />
//                             </Card>
//                           )}
//                         </div>
//                       )}

//                       {/* Metric-card grid */}
//                       {hasCards && (
//                         <div
//                           style={{
//                             display: "grid",
//                             gridTemplateColumns:
//                               "repeat(auto-fill, minmax(200px, 1fr))",
//                             gap: 14,
//                           }}
//                         >
//                           {counts.map((rep, idx) => (
//                             <CountData key={`count-${idx}`} data={rep} />
//                           ))}
//                           {countlist.map((rep, idx) => (
//                             <PopUpData
//                               key={`cl-${idx}`}
//                               data={rep}
//                               title={rep.check_title}
//                               check={rep.check}
//                               type="count"
//                               indexing={rep?.content?.[0]?.index}
//                             />
//                           ))}
//                           {counttable.map((rep, idx) => (
//                             <TablePopUp
//                               key={`ct-${idx}`}
//                               data={rep}
//                               title={rep.check_title}
//                               check={rep.check}
//                               type="count"
//                             />
//                           ))}
//                           {truefalselist.map((rep, idx) => (
//                             <PopUpData
//                               key={`tfl-${idx}`}
//                               data={rep}
//                               title={rep.check_title}
//                               check={rep.check}
//                               type="truefalse"
//                               indexing={rep?.content?.[0]?.index}
//                             />
//                           ))}
//                           {truefalsetable.map((rep, idx) => (
//                             <TablePopUp
//                               key={`tft-${idx}`}
//                               data={rep}
//                               title={rep.check_title}
//                               check={rep.check}
//                               type="truefalse"
//                               content={rep?.content?.[0]?.content}
//                             />
//                           ))}
//                           {truefalse.map((rep, idx) => (
//                             <TrueFalseData key={`tf-${idx}`} data={rep} />
//                           ))}
//                         </div>
//                       )}

//                       {/* Nested table-popup cards (wider layout) */}
//                       {tablewithpopup.length > 0 && (
//                         <div
//                           style={{
//                             display: "grid",
//                             gridTemplateColumns:
//                               "repeat(auto-fit, minmax(320px, 1fr))",
//                             gap: 20,
//                           }}
//                         >
//                           {tablewithpopup.map((rep, idx) => (
//                             <NestedTablePopUp
//                               key={`ntp-${idx}`}
//                               data={rep}
//                               title={rep.check_title}
//                             />
//                           ))}
//                         </div>
//                       )}
//                     </div>
//                   </section>
//                 );
//               })}
//             </div>

//             {/* ── Footer ────────────────────────────────────────────────── */}
//             <div
//               style={{
//                 borderTop: `1px solid ${C.border}`,
//                 backgroundColor: C.card,
//                 padding: "18px 40px",
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//               }}
//             >
//               <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
//                 Article Quality Report ·{" "}
//                 {new Date().toLocaleDateString("en-GB", {
//                   day: "2-digit",
//                   month: "short",
//                   year: "numeric",
//                 })}
//               </p>
//               <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
//                 {data?.length ?? 0} total checks performed
//               </p>
//             </div>
//           </>
//         ) : (
//           /* ── Empty / in-progress state ──────────────────────────────── */
//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               minHeight: "70vh",
//             }}
//           >
//             <div
//               style={{
//                 backgroundColor: C.card,
//                 borderRadius: 20,
//                 padding: "52px 64px",
//                 textAlign: "center",
//                 boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
//                 border: `1px solid ${C.border}`,
//                 maxWidth: 420,
//               }}
//             >
//               <p style={{ fontSize: 44, margin: "0 0 20px" }}>
//                 {emptyState.icon}
//               </p>
//               <span
//                 style={{
//                   fontSize: 14,
//                   fontWeight: 700,
//                   padding: "7px 20px",
//                   borderRadius: 999,
//                   display: "inline-block",
//                   backgroundColor: emptyState.bg,
//                   color: emptyState.color,
//                   marginBottom: 16,
//                 }}
//               >
//                 {emptyState.label}
//               </span>
//               <p
//                 style={{
//                   fontSize: 13,
//                   color: C.textMuted,
//                   margin: "12px 0 0",
//                   lineHeight: 1.6,
//                 }}
//               >
//                 You can navigate away and return — the report will be ready
//                 shortly.
//               </p>
//             </div>
//           </div>
//         )}
//       </div>
//     </LoadingScreen>
//   );
// };

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgCharts } from "ag-charts-react";
import { article_details } from "../api";
import LoadingScreen from "react-loading-screen";
import html2canvas from "html2canvas";
import Modal from "./Modal";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";

/* ------------------------------------------------------------------ */
/*  Design tokens                                                      */
/* ------------------------------------------------------------------ */
const BRAND = {
  navy: "#154A79",
  amber: "#FEB13F",
  green: "#1B8A5A",
  red: "#D64545",
};

const HEADER_OFFSET = 102; // px — used for sticky sidebar / scroll-margin

/* ------------------------------------------------------------------ */
/*  Icons — lightweight inline SVGs, no extra dependency required      */
/* ------------------------------------------------------------------ */
const Icon = ({ children, size = 16, className = "", ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    {children}
  </svg>
);

const IconDownload = (p) => (
  <Icon {...p}>
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </Icon>
);
const IconFileText = (p) => (
  <Icon {...p}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </Icon>
);
const IconInfo = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Icon>
);
const IconClose = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Icon>
);
const IconCheckCircle = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="m9 11 3 3L22 4" />
  </Icon>
);
const IconXCircle = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </Icon>
);
const IconBarChart = (p) => (
  <Icon {...p}>
    <path d="M3 3v18h18" />
    <path d="M7 16v-4" />
    <path d="M12 16V8" />
    <path d="M17 16v-7" />
  </Icon>
);
const IconPieChart = (p) => (
  <Icon {...p}>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </Icon>
);
const IconTable = (p) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M9 4v16" />
  </Icon>
);
const IconHash = (p) => (
  <Icon {...p}>
    <path d="M4 9h16" />
    <path d="M4 15h16" />
    <path d="M10 3 8 21" />
    <path d="M16 3l-2 18" />
  </Icon>
);
const IconCheckSquare = (p) => (
  <Icon {...p}>
    <path d="m9 11 3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </Icon>
);
const IconInbox = (p) => (
  <Icon {...p}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </Icon>
);
const IconClock = (p) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </Icon>
);
const IconLoader = (p) => (
  <Icon className="animate-spin" {...p}>
    <path d="M12 2v4" />
    <path d="m16.2 7.8 2.9-2.9" />
    <path d="M18 12h4" />
    <path d="m16.2 16.2 2.9 2.9" />
    <path d="M12 18v4" />
    <path d="m4.9 19.1 2.9-2.9" />
    <path d="M2 12h4" />
    <path d="m4.9 4.9 2.9 2.9" />
  </Icon>
);
const IconAlertTriangle = (p) => (
  <Icon {...p}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </Icon>
);
const IconSearch = (p) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </Icon>
);
const IconChevronDown = (p) => (
  <Icon strokeWidth="2.3" {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);
const IconLayers = (p) => (
  <Icon {...p}>
    <path d="m12 2 9 5-9 5-9-5 9-5Z" />
    <path d="m3 12 9 5 9-5" />
    <path d="m3 17 9 5 9-5" />
  </Icon>
);

/* ------------------------------------------------------------------ */
/*  Value helpers                                                       */
/* ------------------------------------------------------------------ */
const getCountValue = (rep) => {
  const fromContent =
    Array.isArray(rep?.content) && rep.content.length > 0
      ? rep.content[0]?.count
      : undefined;
  const raw = fromContent !== undefined ? fromContent : rep?.count;
  if (raw === undefined || raw === null || raw === "")
    return { hasData: false };
  return { hasData: true, value: raw === 0 ? "N/A" : raw };
};

const getBoolValue = (rep) => {
  const fromContent =
    Array.isArray(rep?.content) && rep.content?.[0]?.ifexists !== undefined
      ? rep.content[0].ifexists
      : undefined;
  const raw = fromContent !== undefined ? fromContent : rep?.ifexits;
  if (raw === undefined || raw === null || raw === "")
    return { hasData: false };
  const str = raw.toString();
  return {
    hasData: true,
    isTrue: str.toLowerCase() === "true",
    label: str.charAt(0).toUpperCase() + str.slice(1),
  };
};

const isEmptyArray = (arr) => !Array.isArray(arr) || arr.length === 0;

const METRIC_META = {
  count: { valueType: "count", detail: null },
  count_list: { valueType: "count", detail: "list" },
  count_table: { valueType: "count", detail: "table" },
  true_false: { valueType: "bool", detail: null },
  true_false_list: { valueType: "bool", detail: "list" },
  true_false_table: { valueType: "bool", detail: "table" },
};

/** Whether a report row carries any renderable value, used for the coverage stat */
const reportHasValue = (rep) => {
  const type = rep?.display_type?.trim().toLowerCase();
  if (["count", "count_list", "count_table"].includes(type))
    return getCountValue(rep).hasData;
  if (["true_false", "true_false_list", "true_false_table"].includes(type))
    return getBoolValue(rep).hasData;
  if (type === "table_popup")
    return Array.isArray(rep?.content) && rep.content.length > 0;
  if (type === "chart") return rep?.count !== undefined && rep?.count !== null;
  if (type === "histogram")
    return (
      Array.isArray(rep?.content?.[0]?.content) &&
      rep.content[0].content.length > 1
    );
  if (type === "donut_chart")
    return rep?.count !== undefined && rep?.count !== null && rep?.count !== 0;
  return false;
};

/* ------------------------------------------------------------------ */
/*  Shared "no data" building blocks                                    */
/* ------------------------------------------------------------------ */
const ChartEmptyState = ({
  title = "No data available",
  message = "There isn't enough data to render this yet.",
  height = 300,
}) => (
  <div
    style={{ minHeight: height }}
    className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-6 text-center"
  >
    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-300">
      <IconBarChart size={20} />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-500">{title}</p>
      <p className="mx-auto mt-1 max-w-[260px] text-xs leading-relaxed text-slate-400">
        {message}
      </p>
    </div>
  </div>
);

const TableEmptyRow = ({ colSpan, message = "No relevant data found" }) => (
  <tr>
    <td colSpan={colSpan || 1} className="py-8">
      <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
        <IconInbox size={20} />
        <p className="text-xs font-medium">{message}</p>
      </div>
    </td>
  </tr>
);

const NoValueBadge = () => (
  <span
    className="text-2xl font-semibold text-slate-300"
    title="No data recorded"
  >
    —
  </span>
);

const BoolBadge = ({ isTrue, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${
      isTrue ? "bg-[#EAF7F1] text-[#1B8A5A]" : "bg-[#FDEDED] text-[#D64545]"
    }`}
  >
    {isTrue ? <IconCheckCircle size={14} /> : <IconXCircle size={14} />}
    {label}
  </span>
);

/** Consistent card shell used for charts, histograms, donuts and tables */
const SectionCard = ({ icon, title, className = "", children }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md ${className}`}
  >
    <div className="mb-4 flex items-center gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#154A79]/[0.06] text-[#154A79]">
        {icon}
      </span>
      <p className="truncate text-sm font-semibold text-[#154A79]">{title}</p>
    </div>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */
const Dynamicgraph = ({ id }) => {
  const [data, setData] = useState([]);
  const [category, setCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileStatus, setFileStatus] = useState("");
  const [excelData, setExcelData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);

  const navigate = useNavigate();
  const pdfRef = useRef();
  const sectionRefs = useRef({});

  useEffect(() => {
    fetchreport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (data) {
      const exceldata = data.map((item) => ({
        CheckNo: item.check,
        CheckTitle: item.check_title,
        Count: item.count,
      }));
      setExcelData(exceldata);
    }
  }, [data]);

  useEffect(() => {
    window.data = data;
  }, [data]);

  const fetchreport = async () => {
    const TIMEOUT = 50000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timeout")), TIMEOUT),
    );
    try {
      const result = await Promise.race([article_details(id), timeoutPromise]);
      if (result.data) {
        setFileStatus(result?.data?.file_progress);
        setLoading(false);
        setData(result?.data?.report);
        setCategory(result?.data?.category);
      } else {
        toast.warn(result.error.message);
        navigate("/filehistory");
      }
    } catch (e) {
      if (e.message === "Timeout") {
        toast.warn("Response took longer than expected, Redirecting ...");
        navigate("/filehistory");
      } else {
        console.log(e?.response?.data?.message || e.message);
      }
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Derived data: search filtering, grouping, coverage stats           */
  /* ---------------------------------------------------------------- */
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;
    return data.filter((rep) => rep?.check_title?.toLowerCase().includes(term));
  }, [data, searchTerm]);

  const sortedReport = useMemo(() => {
    return filteredData.reduce((group, item) => {
      if (!group[item.category_id]) group[item.category_id] = [];
      group[item.category_id].push(item);
      return group;
    }, {});
  }, [filteredData]);

  const visibleCategories = useMemo(
    () =>
      (category || []).filter(
        (cat) => (sortedReport[cat.category] || []).length > 0,
      ),
    [category, sortedReport],
  );

  const stats = useMemo(() => {
    const total = data.length;
    const missing = data.filter((rep) => !reportHasValue(rep)).length;
    const coverage =
      total > 0 ? Math.round(((total - missing) / total) * 100) : 0;
    return { total, missing, coverage, categories: visibleCategories.length };
  }, [data, visibleCategories]);

  /* ---------------------------------------------------------------- */
  /*  Scrollspy — highlight the active category in the sidebar           */
  /* ---------------------------------------------------------------- */
  useEffect(() => {
    if (
      !visibleCategories.length ||
      typeof IntersectionObserver === "undefined"
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.getAttribute("data-cat"));
          }
        });
      },
      { rootMargin: `-${HEADER_OFFSET + 20}px 0px -70% 0px`, threshold: 0 },
    );

    Object.values(sectionRefs.current).forEach(
      (el) => el && observer.observe(el),
    );
    return () => observer.disconnect();
  }, [visibleCategories]);

  const scrollToCategory = (catId) => {
    const el = document.getElementById(`cat-${catId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleCollapsed = (catId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(catId) ? next.delete(catId) : next.add(catId);
      return next;
    });
  };

  /* ---------------------------------------------------------------- */
  /*  Charts                                                             */
  /* ---------------------------------------------------------------- */
  const BarChartCard = ({ items }) => {
    const isEmpty =
      isEmptyArray(items) ||
      items.every((d) => d?.count === undefined || d?.count === null);

    if (isEmpty) {
      return (
        <SectionCard
          icon={<IconBarChart size={16} />}
          title="Checks Breakdown"
          className="sm:col-span-2"
        >
          <ChartEmptyState
            title="No check data to chart"
            message="Once checks are recorded for this report, they'll appear here as a bar chart."
          />
        </SectionCard>
      );
    }

    const options = {
      data: items,
      series: [
        {
          type: "bar",
          direction: "horizontal",
          xKey: "check_title",
          yKey: "count",
          yName: "Checks",
          fill: BRAND.amber,
          label: {
            enabled: true,
            color: "#334155",
            fontSize: 12,
            placement: "outside",
            formatter: ({ value }) => value.toString(),
          },
        },
      ],
      height: 300,
      background: { fill: "transparent" },
    };

    return (
      <SectionCard
        icon={<IconBarChart size={16} />}
        title="Checks Breakdown"
        className="sm:col-span-2"
      >
        <AgCharts options={options} />
      </SectionCard>
    );
  };

  const HistogramCard = ({ rep }) => {
    let chartData = [];
    try {
      if (rep?.content?.[0]?.content?.length > 1) {
        chartData = rep.content[0].content.map((entry) => {
          const key = Object.keys(entry)[0];
          return { name: key.trim(), value: parseInt(entry[key].trim(), 10) };
        });
      }
    } catch (e) {
      chartData = [];
    }

    if (chartData.length === 0) {
      return (
        <SectionCard
          icon={<IconBarChart size={16} />}
          title="Reference Year Histogram"
          className="sm:col-span-2"
        >
          <ChartEmptyState
            title="No reference-year data"
            message="This histogram populates once reference year entries are available."
          />
        </SectionCard>
      );
    }

    const options = {
      data: chartData,
      series: [
        {
          type: "bar",
          xKey: "name",
          yKey: "value",
          yName: "Checks",
          fill: BRAND.amber,
          label: {
            enabled: true,
            color: "#334155",
            fontSize: 12,
            placement: "outside",
            formatter: ({ value }) => value.toString(),
          },
        },
      ],
      height: 300,
      background: { fill: "transparent" },
    };

    return (
      <SectionCard
        icon={<IconBarChart size={16} />}
        title="Reference Year Histogram"
        className="sm:col-span-2"
      >
        <AgCharts options={options} />
      </SectionCard>
    );
  };

  const DonutCard = ({ items }) => {
    const isSingle = !Array.isArray(items);
    const chartData = isSingle
      ? items
        ? [{ check_title: items.check_title, count: items.count }]
        : []
      : items;

    const isEmpty =
      chartData.length === 0 ||
      chartData.every(
        (d) => d?.count === undefined || d?.count === null || d?.count === 0,
      );

    const title = isSingle
      ? items?.check_title || "Distribution"
      : "Distribution";

    if (isEmpty) {
      return (
        <SectionCard
          icon={<IconPieChart size={16} />}
          title={title}
          className="sm:col-span-2"
        >
          <ChartEmptyState
            title="No distribution data"
            message="This breakdown appears as a donut chart once values are available."
          />
        </SectionCard>
      );
    }

    const options = {
      legend: { position: "bottom" },
      series: [
        {
          type: "donut",
          angleKey: "count",
          calloutLabelKey: "check_title",
          sectorLabelKey: "count",
          innerRadiusRatio: 0.6,
          data: chartData,
          fills: [BRAND.navy, BRAND.amber, "#7FB3D5", BRAND.green],
        },
      ],
      height: 300,
      background: { fill: "transparent" },
    };

    return (
      <SectionCard
        icon={<IconPieChart size={16} />}
        title={title}
        className="sm:col-span-2"
      >
        <AgCharts options={options} />
      </SectionCard>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Metric card (count / true-false, with optional detail modal)       */
  /* ---------------------------------------------------------------- */
  const MetricCard = ({ rep }) => {
    const type = rep?.display_type?.trim().toLowerCase();
    const meta = METRIC_META[type];
    if (!meta) return null;

    const count = meta.valueType === "count" ? getCountValue(rep) : null;
    const bool = meta.valueType === "bool" ? getBoolValue(rep) : null;

    const hasDetailContent =
      meta.detail === "list"
        ? Array.isArray(rep?.content) &&
          rep?.content?.[0]?.content &&
          rep?.content?.[0]?.content?.length > 0
        : meta.detail === "table"
          ? !!rep?.content?.[0]?.content
          : false;

    return (
      <div className="group flex min-h-[136px] flex-col justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#FEB13F]/60 hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
            {meta.valueType === "bool" ? (
              <IconCheckSquare size={14} />
            ) : (
              <IconHash size={14} />
            )}
          </span>
          {hasDetailContent && (
            <button
              onClick={() => setActiveDetail({ rep, kind: meta.detail })}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-[#FEB13F] hover:bg-[#FFF4E1] hover:text-[#154A79]"
              title="View details"
            >
              <IconInfo size={12} />
            </button>
          )}
        </div>

        <div>
          <p className="mb-1.5 line-clamp-2 text-[13px] font-medium leading-snug text-slate-500">
            {rep?.check_title}
          </p>
          {meta.valueType === "bool" ? (
            bool.hasData ? (
              <BoolBadge isTrue={bool.isTrue} label={bool.label} />
            ) : (
              <NoValueBadge />
            )
          ) : count.hasData ? (
            <span
              className={`${
                typeof count.value === "string" && count.value.length > 5
                  ? "text-lg"
                  : "text-2xl"
              } font-bold text-[#154A79]`}
            >
              {count.value}
            </span>
          ) : (
            <NoValueBadge />
          )}
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Shared detail modal for list / table metric cards                  */
  /* ---------------------------------------------------------------- */
  const DetailModal = () => {
    if (!activeDetail) return null;
    const { rep, kind } = activeDetail;
    const indexing = rep?.content?.[0]?.index;

    const tableHeaders =
      kind === "table" && Array.isArray(rep?.content?.[0]?.content)
        ? [
            ...new Set(
              rep.content[0].content.flatMap((obj) => Object.keys(obj || {})),
            ),
          ]
        : [];

    const hasNestedContent =
      kind === "table" &&
      Array.isArray(rep?.content) &&
      rep.content.some((item) => typeof item?.content === "object");

    return (
      <Modal show={!!activeDetail}>
        <div className="flex w-full max-h-[520px] flex-col gap-3 rounded-2xl p-5">
          <div className="flex w-full items-center justify-between">
            <h2 className="text-lg font-semibold text-[#154A79]">
              {rep?.check_title}
            </h2>
            <button
              onClick={() => setActiveDetail(null)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-[#FEB13F] hover:text-white"
            >
              <IconClose />
            </button>
          </div>

          {kind === "list" && indexing && (
            <div className="flex w-full flex-col gap-2 rounded-xl bg-slate-50 p-3">
              <div className="flex w-full items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-[#D64545]" />
                <p className="text-sm text-slate-600">{indexing?.title1}</p>
              </div>
              <div className="flex w-full items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-[#1B8A5A]" />
                <p className="text-sm text-slate-600">{indexing?.title2}</p>
              </div>
            </div>
          )}

          <hr className="w-full border-slate-100" />

          <div className="mt-1 w-full overflow-y-auto p-1">
            {kind === "list" ? (
              Array.isArray(rep?.content?.[0]?.content) &&
              rep.content[0].content.length > 0 ? (
                <ul className="list-disc space-y-3 pl-5 text-sm text-slate-700">
                  {rep.content[0].content.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : Array.isArray(rep?.content) && rep.content.length > 0 ? (
                <ul className="list-disc space-y-3 pl-5 text-sm text-slate-700">
                  {rep.content.map((item, i) => (
                    <li key={i}>{item.content}</li>
                  ))}
                </ul>
              ) : (
                <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
                  <IconInbox size={20} />
                  <p className="text-xs font-medium">No relevant data found</p>
                </div>
              )
            ) : hasNestedContent ? (
              <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
                <IconAlertTriangle size={20} />
                <p className="text-xs font-medium">
                  Nested content detected. Please select a different display
                  type.
                </p>
              </div>
            ) : (
              <div className="max-h-[320px] overflow-y-auto rounded-xl border border-slate-200">
                <table className="w-full border-collapse text-sm">
                  {tableHeaders.length > 0 && (
                    <thead className="sticky top-0 bg-slate-50">
                      <tr>
                        {tableHeaders.map((key, i) => (
                          <th
                            key={i}
                            className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody className="divide-y divide-slate-100">
                    {Array.isArray(rep?.content?.[0]?.content) &&
                    rep.content[0].content.length > 0 ? (
                      rep.content[0].content.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          {tableHeaders.map((header, j) => (
                            <td key={j} className="px-3 py-2 text-slate-600">
                              {row?.[header] ?? "N/A"}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <TableEmptyRow colSpan={tableHeaders.length || 1} />
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Full data-table card (table_popup)                                 */
  /* ---------------------------------------------------------------- */
  const DataTableCard = ({ rep }) => {
    const [rowModal, setRowModal] = useState(null);

    const isEmptyContent =
      !rep?.content || !Array.isArray(rep.content) || rep.content.length === 0;

    const allKeys =
      rep?.check === 41
        ? ["id", "author", "orcid"].filter((key) =>
            rep?.content?.some((item) => key in (item || {})),
          )
        : Array.from(
            new Set(
              rep?.content?.flatMap((item) => Object.keys(item || {})) || [],
            ),
          );

    const hasContentKey = rep?.content?.some(
      (item) => "content" in (item || {}),
    );
    const displayKeys = hasContentKey
      ? allKeys.filter((key) => key !== "content")
      : allKeys;

    const renderValue = (val) => {
      if (val == null || val === "NULL") return "N/A";
      if (Array.isArray(val))
        return val.length > 0 ? val.join(", ") : "No relevant data found";
      if (typeof val === "object") return JSON.stringify(val);
      return val.toString();
    };

    const isNestedContent = (content) => {
      if (!content) return false;
      if (Array.isArray(content)) {
        return content.some(
          (item) => typeof item === "object" && !Array.isArray(item),
        );
      }
      return typeof content === "object";
    };

    const renderNestedTable = (nested) => {
      if (!nested || (Array.isArray(nested) && nested.length === 0)) {
        return (
          <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
            <IconInbox size={20} />
            <p className="text-xs font-medium">No relevant data found</p>
          </div>
        );
      }

      if (
        Array.isArray(nested) &&
        nested.every((item) => typeof item === "object")
      ) {
        const nestedKeys = Array.from(
          new Set(nested.flatMap((obj) => Object.keys(obj || {}))),
        );
        return (
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                {nestedKeys.map((key) => (
                  <th
                    key={key}
                    className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {nested.map((obj, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  {nestedKeys.map((key) => (
                    <td key={key} className="px-3 py-2 text-slate-600">
                      {renderValue(obj[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      }

      if (Array.isArray(nested)) {
        return (
          <ul className="list-disc space-y-2 pl-5 text-sm text-slate-700">
            {nested.map((item, idx) => (
              <li key={idx}>{renderValue(item)}</li>
            ))}
          </ul>
        );
      }

      if (typeof nested === "object") {
        const keys = Object.keys(nested);
        return (
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-50">
              <tr>
                {keys.map((key) => (
                  <th
                    key={key}
                    className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {keys.map((key) => (
                  <td key={key} className="px-3 py-2 text-slate-600">
                    {renderValue(nested[key])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        );
      }

      return <p className="text-sm text-slate-600">{renderValue(nested)}</p>;
    };

    return (
      <SectionCard
        icon={<IconTable size={16} />}
        title={rep?.check_title}
        className="sm:col-span-2"
      >
        <div className="max-h-[280px] overflow-auto rounded-xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                {displayKeys.map((key) => (
                  <th
                    key={key}
                    className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {key}
                  </th>
                ))}
                {hasContentKey && (
                  <th className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Content
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isEmptyContent ? (
                <TableEmptyRow
                  colSpan={displayKeys.length + (hasContentKey ? 1 : 0)}
                />
              ) : (
                rep.content.map((row, rowIndex) => {
                  const nested = row.content;
                  const nestedFlag = isNestedContent(nested);
                  return (
                    <tr key={rowIndex} className="hover:bg-slate-50">
                      {displayKeys.map((key) => (
                        <td key={key} className="px-3 py-2 text-slate-600">
                          {renderValue(row[key])}
                        </td>
                      ))}
                      {hasContentKey && (
                        <td className="px-3 py-2 text-center">
                          {nestedFlag ? (
                            <button
                              className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-[#FEB13F] hover:bg-[#FFF4E1] hover:text-[#154A79]"
                              onClick={() => setRowModal(rowIndex)}
                              title="View details"
                            >
                              <IconInfo size={12} />
                            </button>
                          ) : (
                            <span className="text-slate-600">
                              {renderValue(nested)}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {rowModal !== null && (
          <Modal show={true}>
            <div className="flex w-full flex-col gap-3 rounded-2xl p-5">
              <div className="flex w-full items-center justify-between">
                <h2 className="text-lg font-semibold text-[#154A79]">
                  {rep?.check_title} — Row {rowModal + 1}
                </h2>
                <button
                  onClick={() => setRowModal(null)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-[#FEB13F] hover:text-white"
                >
                  <IconClose />
                </button>
              </div>
              <hr className="w-full border-slate-100" />
              <div className="mt-1 max-h-[320px] w-full overflow-y-auto rounded-xl border border-slate-200">
                {renderNestedTable(rep.content[rowModal]?.content)}
              </div>
            </div>
          </Modal>
        )}
      </SectionCard>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Export helpers (unchanged logic)                                   */
  /* ---------------------------------------------------------------- */
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const reportdata = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(reportdata, "report.xlsx");
  };

  const generatePDF = () => {
    const input = pdfRef.current;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
      pdf.save("report.pdf");
    });
  };

  /* ---------------------------------------------------------------- */
  /*  Full-page status / empty states                                    */
  /* ---------------------------------------------------------------- */
  const StatusView = () => {
    const map = {
      1: {
        icon: <IconClock size={26} />,
        title: "Not Started Yet",
        message: "Report generation hasn't begun. Check back shortly.",
      },
      2: {
        icon: <IconLoader size={26} />,
        title: "In Progress",
        message: "We're generating this report — it should be ready soon.",
      },
    };
    const config = map[fileStatus] || {
      icon: <IconAlertTriangle size={26} />,
      title: "Something Went Wrong",
      message: "There was an error while generating this report.",
      error: true,
    };

    return (
      <div className="flex h-[70vh] w-full items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div
            className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${
              config.error
                ? "bg-[#FDEDED] text-[#D64545]"
                : "bg-slate-50 text-slate-400"
            }`}
          >
            {config.icon}
          </div>
          <h3 className="mb-1 text-lg font-semibold text-[#154A79]">
            {config.title}
          </h3>
          <p className="text-sm text-slate-500">{config.message}</p>
        </div>
      </div>
    );
  };

  const NoSearchResults = () => (
    <div className="flex h-[40vh] w-full flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-300">
        <IconSearch size={20} />
      </div>
      <p className="text-sm font-semibold text-slate-500">
        No checks match “{searchTerm}”
      </p>
      <button
        onClick={() => setSearchTerm("")}
        className="text-sm font-medium text-[#154A79] underline underline-offset-2"
      >
        Clear search
      </button>
    </div>
  );

  const StatCard = ({ icon, label, value, tone = "default" }) => {
    const tones = {
      default: "text-[#154A79] bg-[#154A79]/[0.06]",
      warn: "text-[#D64545] bg-[#FDEDED]",
      good: "text-[#1B8A5A] bg-[#EAF7F1]",
    };
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-slate-400">{label}</p>
          <p className="text-lg font-bold text-slate-800">{value}</p>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                             */
  /* ---------------------------------------------------------------- */
  return (
    <LoadingScreen
      loading={loading}
      bgColor="#f8fafc"
      spinnerColor="#154A79"
      textColor="#475569"
      text="Fetching data, please wait…"
    >
      <div className="min-h-full w-full bg-slate-50">
        {data?.length > 0 ? (
          <>
            {/* Top bar */}
            <header className="sticky top-[80px] z-20 flex w-full items-center justify-between gap-4 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur md:px-8">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-bold text-[#154A79] md:text-2xl">
                  Article Report
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 md:text-sm">
                  Overview and analytics for this submission
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2 md:gap-3">
                <div className="relative hidden sm:block">
                  <IconSearch
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search checks…"
                    className="w-48 rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-600 outline-none transition-colors focus:border-[#FEB13F] focus:bg-white md:w-64"
                  />
                </div>
                <button
                  onClick={() => exportToExcel()}
                  className="flex items-center gap-2 rounded-full border border-slate-200 px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#FEB13F] hover:text-[#154A79] md:px-4"
                >
                  <IconDownload size={15} />
                  <span className="hidden md:inline">Excel</span>
                </button>
                <button
                  onClick={() => generatePDF()}
                  className="flex items-center gap-2 rounded-full border border-slate-200 px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-[#FEB13F] hover:text-[#154A79] md:px-4"
                >
                  <IconFileText size={15} />
                  <span className="hidden md:inline">PDF</span>
                </button>
              </div>
            </header>

            {/* Mobile search */}
            <div className="border-b border-slate-200 bg-white px-6 py-3 sm:hidden">
              <div className="relative">
                <IconSearch
                  size={15}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search checks…"
                  className="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-600 outline-none focus:border-[#FEB13F] focus:bg-white"
                />
              </div>
            </div>

            <div className="flex w-full items-start">
              {/* Sidebar nav — desktop */}
              <aside
                className="sticky hidden h-[calc(100vh-73px)] w-60 shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-3 py-6 lg:block "
                style={{ top: HEADER_OFFSET + 60 }}
              >
                <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Categories
                </p>
                <nav className="flex flex-col gap-1">
                  {visibleCategories.map((cat) => (
                    <button
                      key={cat.category}
                      onClick={() => scrollToCategory(cat.category)}
                      className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                        activeCategory === String(cat.category) ||
                        activeCategory === cat.category
                          ? "bg-[#154A79] text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <span className="truncate">{cat.value}</span>
                      <span
                        className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          activeCategory === String(cat.category) ||
                          activeCategory === cat.category
                            ? "bg-white/20 text-white"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {(sortedReport[cat.category] || []).length}
                      </span>
                    </button>
                  ))}
                </nav>
              </aside>

              {/* Main content */}
              <main className="min-w-0 flex-1 px-6 py-8 md:px-10 mb-80">
                {filteredData.length === 0 ? (
                  <NoSearchResults />
                ) : (
                  <>
                    {/* Coverage stats */}
                    <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <StatCard
                        icon={<IconLayers size={18} />}
                        label="Categories"
                        value={stats.categories}
                      />
                      <StatCard
                        icon={<IconHash size={18} />}
                        label="Total Checks"
                        value={stats.total}
                      />
                      <StatCard
                        icon={<IconCheckSquare size={18} />}
                        label="Data Coverage"
                        value={`${stats.coverage}%`}
                        tone="good"
                      />
                      <StatCard
                        icon={<IconAlertTriangle size={18} />}
                        label="Needs Attention"
                        value={stats.missing}
                        tone={stats.missing > 0 ? "warn" : "good"}
                      />
                    </div>

                    {/* Mobile category chips */}
                    <div className="mb-6 flex gap-2 overflow-x-auto pb-1 lg:hidden">
                      {visibleCategories.map((cat) => (
                        <button
                          key={cat.category}
                          onClick={() => scrollToCategory(cat.category)}
                          className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-[#FEB13F] hover:text-[#154A79]"
                        >
                          {cat.value}
                          <span className="ml-1.5 text-slate-400">
                            {(sortedReport[cat.category] || []).length}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div ref={pdfRef} className="flex flex-col gap-6">
                      {visibleCategories.map((cat) => {
                        const reports = sortedReport[cat.category] || [];
                        const isCollapsed = collapsed.has(cat.category);

                        const charts = reports.filter(
                          (r) =>
                            r?.display_type?.trim().toLowerCase() === "chart",
                        );
                        const histogram = reports.filter(
                          (r) =>
                            r?.display_type?.trim().toLowerCase() ===
                            "histogram",
                        );
                        const donut = reports.filter(
                          (r) =>
                            r?.display_type?.trim().toLowerCase() ===
                            "donut_chart",
                        );
                        const tablewithpopup = reports.filter(
                          (r) =>
                            r?.display_type?.trim().toLowerCase() ===
                            "table_popup",
                        );
                        const metrics = reports.filter((r) =>
                          Object.keys(METRIC_META).includes(
                            r?.display_type?.trim().toLowerCase(),
                          ),
                        );

                        return (
                          <section
                            key={cat.category}
                            id={`cat-${cat.category}`}
                            data-cat={cat.category}
                            ref={(el) => {
                              sectionRefs.current[cat.category] = el;
                            }}
                            style={{ scrollMarginTop: HEADER_OFFSET }}
                            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8"
                          >
                            <button
                              onClick={() => toggleCollapsed(cat.category)}
                              className="mb-6 flex w-full items-center gap-3 text-left"
                            >
                              <span className="h-7 w-1.5 shrink-0 rounded-full bg-[#FEB13F]" />
                              <h3 className="text-xl font-semibold text-[#154A79]">
                                {cat.value}
                              </h3>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                                {reports.length}{" "}
                                {reports.length === 1 ? "check" : "checks"}
                              </span>
                              <IconChevronDown
                                size={18}
                                className={`ml-auto shrink-0 text-slate-400 transition-transform ${
                                  isCollapsed ? "-rotate-90" : ""
                                }`}
                              />
                            </button>

                            {!isCollapsed && (
                              <div className="flex flex-col gap-5">
                                {(charts.length > 0 ||
                                  histogram.length > 0 ||
                                  donut.length > 0) && (
                                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    {charts.length > 0 && (
                                      <BarChartCard items={charts} />
                                    )}
                                    {histogram.map((h, idx) => (
                                      <HistogramCard key={`h-${idx}`} rep={h} />
                                    ))}
                                    {donut.length > 0 && (
                                      <DonutCard items={donut} />
                                    )}
                                  </div>
                                )}

                                {metrics.length > 0 && (
                                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                                    {metrics.map((rep, idx) => (
                                      <MetricCard
                                        key={`metric-${idx}`}
                                        rep={rep}
                                      />
                                    ))}
                                  </div>
                                )}

                                {tablewithpopup.length > 0 && (
                                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    {tablewithpopup.map((rep, idx) => (
                                      <DataTableCard
                                        key={`table-${idx}`}
                                        rep={rep}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </div>
                  </>
                )}
              </main>
            </div>

            <DetailModal />
          </>
        ) : (
          <StatusView />
        )}
      </div>
    </LoadingScreen>
  );
};

export default Dynamicgraph;
